import { RocketOutlined } from '@ant-design/icons';
import ROUTES from 'constants/routes';
import { TFunction } from 'i18next';
import {
	ArrowUpRight,
	BarChart2,
	BellDot,
	Binoculars,
	Book,
	Bot,
	Boxes,
	BugIcon,
	ChartArea,
	Cloudy,
	Database,
	DraftingCompass,
	FileKey2,
	FileText,
	Github,
	HardDrive,
	Home,
	Keyboard,
	Layers2,
	MailCheck,
	LayoutGrid,
	ListMinus,
	MessageSquareText,
	Plus,
	Receipt,
	Route,
	ScrollText,
	ServerCog,
	Settings,
	Shield,
	Slack,
	Sparkles,
	Unplug,
	User,
	UserPlus,
	Users,
} from 'lucide-react';

import {
	SecondaryMenuItemKey,
	SettingsNavSection,
	SidebarItem,
} from './sideNav.types';

export const getStartedMenuItem = {
	key: ROUTES.GET_STARTED,
	label: 'Get Started',
	icon: <RocketOutlined rotate={45} />,
};

export const getStartedV3MenuItem = {
	key: ROUTES.GET_STARTED_WITH_CLOUD,
	label: 'Get Started',
	icon: <RocketOutlined rotate={45} />,
};

export const homeMenuItem = {
	key: ROUTES.HOME,
	label: 'Home',
	icon: <Home size={16} />,
};

export const inviteMemberMenuItem = {
	key: `${ROUTES.ORG_SETTINGS}#invite-team-members`,
	label: 'Invite Team Member',
	icon: <UserPlus size={16} />,
};

export const manageLicenseMenuItem = {
	key: ROUTES.LIST_LICENSES,
	label: 'Manage Licenses',
	icon: <FileKey2 size={16} />,
};

export const helpSupportMenuItem = {
	key: ROUTES.SUPPORT,
	label: 'Help & Support',
	icon: <MessageSquareText size={16} />,
};

export const shortcutMenuItem = {
	key: ROUTES.SHORTCUTS,
	label: 'Keyboard Shortcuts',
	icon: <Layers2 size={16} />,
};

export const slackSupportMenuItem = {
	key: SecondaryMenuItemKey.Slack,
	label: 'Slack Support',
	icon: <Slack size={16} />,
};

export const trySignozCloudMenuItem: SidebarItem = {
	key: 'trySignozCloud',
	label: 'Try Signoz Cloud',
	icon: <Cloudy size={16} />,
};

const menuItems: SidebarItem[] = [
	{
		key: ROUTES.HOME,
		label: 'Home',
		icon: <Home size={16} />,
		itemKey: 'home',
	},
	{
		key: ROUTES.APPLICATION,
		label: 'Services',
		icon: <HardDrive size={16} />,
		itemKey: 'services',
	},

	{
		key: ROUTES.LOGS,
		label: 'Logs',
		icon: <ScrollText size={16} />,
		itemKey: 'logs',
	},
	{
		key: ROUTES.METRICS_EXPLORER,
		label: 'Metrics',
		icon: <BarChart2 size={16} />,
		isNew: false,
		itemKey: 'metrics',
	},
	{
		key: ROUTES.INFRASTRUCTURE_MONITORING_HOSTS,
		label: 'Infra Monitoring',
		icon: <Boxes size={16} />,
		itemKey: 'infrastructure',
	},
	{
		key: ROUTES.ALL_DASHBOARD,
		label: 'Dashboards',
		icon: <LayoutGrid size={16} />,
		itemKey: 'dashboards',
	},
	{
		key: ROUTES.MESSAGING_QUEUES_OVERVIEW,
		label: 'Messaging Queues',
		icon: <ListMinus size={16} />,
		itemKey: 'messaging-queues',
	},
	{
		key: ROUTES.API_MONITORING,
		label: 'External APIs',
		icon: <Binoculars size={16} />,
		isNew: true,
		itemKey: 'external-apis',
	},
	{
		key: ROUTES.LIST_ALL_ALERT,
		label: 'Alerts',
		icon: <BellDot size={16} />,
		itemKey: 'alerts',
	},
	{
		key: ROUTES.INTEGRATIONS,
		label: 'Integrations',
		icon: <Unplug size={16} />,
		itemKey: 'integrations',
	},
	{
		key: ROUTES.ALL_ERROR,
		label: 'Exceptions',
		icon: <BugIcon size={16} />,
		itemKey: 'exceptions',
	},
	{
		key: ROUTES.SERVICE_MAP,
		label: 'Service Map',
		icon: <Route size={16} />,
		isBeta: true,
		itemKey: 'service-map',
	},
	{
		key: ROUTES.BILLING,
		label: 'Billing',
		icon: <Receipt size={16} />,
		itemKey: 'billing',
	},
	{
		key: ROUTES.SETTINGS,
		label: 'Settings',
		icon: <Settings size={16} />,
		itemKey: 'settings',
	},
];

// 장애 대응은 알림 -> 대시보드 -> 서비스/트레이스 -> 로그 순으로 흐른다.
// 이 6개는 MORE 섹션에 접히면 매번 펼쳐야 하므로 상시 노출로 둔다.
export const primaryMenuItems: SidebarItem[] = [
	{
		key: ROUTES.HOME,
		label: 'Home',
		icon: <Home size={16} />,
		itemKey: 'home',
	},
	{
		key: ROUTES.LIST_ALL_ALERT,
		label: 'Alerts',
		icon: <BellDot size={16} />,
		itemKey: 'alerts',
	},
	{
		key: ROUTES.ALL_DASHBOARD,
		label: 'Dashboards',
		icon: <LayoutGrid size={16} />,
		itemKey: 'dashboards',
	},
	{
		key: ROUTES.APPLICATION,
		label: 'Services',
		icon: <HardDrive size={16} />,
		itemKey: 'services',
	},
	{
		key: ROUTES.TRACES_EXPLORER,
		label: 'Traces',
		icon: <DraftingCompass size={16} />,
		itemKey: 'traces',
	},
	{
		key: ROUTES.LOGS,
		label: 'Logs',
		icon: <ScrollText size={16} />,
		itemKey: 'logs',
	},
];

export const defaultMoreMenuItems: SidebarItem[] = [
	{
		key: ROUTES.METRICS_EXPLORER,
		label: 'Metrics',
		icon: <BarChart2 size={16} />,
		isNew: false,
		isEnabled: true,
		itemKey: 'metrics',
	},
	{
		key: ROUTES.INFRASTRUCTURE_MONITORING_HOSTS,
		label: 'Infrastructure',
		icon: <Boxes size={16} />,
		isEnabled: true,
		itemKey: 'infrastructure',
	},
	{
		key: ROUTES.SERVICE_MAP,
		label: 'Service Map',
		icon: <Route size={16} />,
		isEnabled: true,
		itemKey: 'service-map',
	},
	{
		key: ROUTES.INTEGRATIONS,
		label: 'Integrations',
		icon: <Unplug size={16} />,
		isEnabled: true,
		itemKey: 'integrations',
	},
	// 예외는 「오류 종류별로 찾아 들어가는」 동선의 출발점이라 표면에 되돌린다.
	// 숨겼던 근거는 "예외 정보를 트레이스에서 문맥과 함께 볼 수 있다"였는데,
	// 그건 트레이스를 «이미 찾은» 뒤의 이야기다. 어떤 오류가 나는지부터 훑어야 하는
	// 사용자에게는 진입점이 없었다. 이 화면은 예외 유형별 집계 → 스택 트레이스 →
	// 해당 스팬이 선택된 트레이스 상세로 이어진다.
	//
	// 한계를 알고 쓴다: 예외 «이벤트»가 있는 오류만 잡힌다. 예외를 남기지 않는
	// 계측(예: 이 estate 의 legacy-web)은 여기 나오지 않으므로, has_error 기반인
	// 트레이스 목록의 오류 표시와 함께 봐야 전체가 보인다.
	{
		key: ROUTES.ALL_ERROR,
		label: 'Exceptions',
		icon: <BugIcon size={16} />,
		isEnabled: true,
		itemKey: 'exceptions',
	},
	// 아래 셋은 개발자·ITO 운영자가 거의 열지 않아 내비게이션 표면에서 숨긴다.
	// 라우트는 그대로 살아 있으므로 URL 직접 접근과 기존 북마크는 계속 동작한다.
	{
		key: ROUTES.API_MONITORING,
		label: 'External APIs',
		icon: <Binoculars size={16} />,
		isNew: true,
		isEnabled: false,
		itemKey: 'external-apis',
	},
	{
		key: ROUTES.METER,
		label: 'Cost Meter',
		icon: <ChartArea size={16} />,
		isNew: false,
		isEnabled: false,
		isBeta: false,
		itemKey: 'meter-explorer',
	},
	{
		key: ROUTES.MESSAGING_QUEUES_OVERVIEW,
		label: 'Messaging Queues',
		icon: <ListMinus size={16} />,
		isEnabled: false,
		itemKey: 'messaging-queues',
	},
];

export const settingsNavSections: SettingsNavSection[] = [
	{
		key: 'data',
		title: 'Data',
		items: [
			{
				key: ROUTES.SETTINGS,
				label: 'Data Retention',
				icon: <Database size={16} />,
				isEnabled: true,
				itemKey: 'workspace',
			},
			{
				key: ROUTES.INGESTION_SETTINGS,
				label: 'Ingestion',
				icon: <RocketOutlined rotate={45} />,
				isEnabled: false,
				itemKey: 'ingestion',
			},
			{
				key: ROUTES.INTEGRATIONS,
				label: 'Integrations',
				icon: <Unplug size={16} />,
				isEnabled: false,
				itemKey: 'integrations',
			},
		],
	},
	{
		key: 'ai-automation',
		title: 'AI & Automation',
		items: [
			{
				key: ROUTES.AI_MODULE_SETTINGS,
				label: 'AI Module',
				icon: <Sparkles size={16} />,
				isEnabled: false,
				itemKey: 'ai-module',
			},
			{
				key: ROUTES.CODE_RCA_SETTINGS,
				label: 'Code RCA',
				icon: <Sparkles size={16} />,
				isEnabled: false,
				itemKey: 'code-rca',
			},
			{
				key: ROUTES.PII_PATTERN_SETTINGS,
				label: 'PII Masking Patterns',
				icon: <Shield size={16} />,
				isEnabled: false,
				itemKey: 'pii-patterns',
			},
			{
				key: ROUTES.REMEDIATION_TARGETS_SETTINGS,
				label: 'Remediation Targets',
				icon: <ServerCog size={16} />,
				isEnabled: false,
				itemKey: 'remediation-targets',
			},
			{
				key: ROUTES.INCIDENT_REPORT_SETTINGS,
				label: 'Incident Report',
				icon: <Sparkles size={16} />,
				isEnabled: false,
				itemKey: 'incident-report',
			},
			{
				key: ROUTES.WEEKLY_REPORT_SETTINGS,
				label: 'Weekly Report',
				icon: <MailCheck size={16} />,
				isEnabled: false,
				itemKey: 'weekly-report',
			},
			{
				key: ROUTES.SOP_DOCUMENTS_SETTINGS,
				label: 'SOP Documents',
				icon: <FileText size={16} />,
				isEnabled: true,
				itemKey: 'sop-documents',
			},
			{
				key: ROUTES.MCP_SERVER,
				label: 'MCP Server',
				icon: <Sparkles size={16} />,
				isEnabled: false,
				itemKey: 'mcp-server',
			},
		],
	},
	{
		key: 'alerts',
		title: 'Alerts',
		items: [
			{
				key: ROUTES.ALL_CHANNELS,
				label: 'Notification Channels',
				icon: <FileKey2 size={16} />,
				isEnabled: true,
				itemKey: 'notification-channels',
			},
		],
	},
	{
		key: 'identity-access',
		title: 'Identity & Access',
		items: [
			{
				key: ROUTES.ROLES_SETTINGS,
				label: 'Roles',
				icon: <Shield size={16} />,
				isEnabled: false,
				itemKey: 'roles',
			},
			{
				key: ROUTES.MEMBERS_SETTINGS,
				label: 'Members',
				icon: <Users size={16} />,
				isEnabled: false,
				itemKey: 'members',
			},
			{
				key: ROUTES.SERVICE_ACCOUNTS_SETTINGS,
				label: 'Service Accounts',
				icon: <Bot size={16} />,
				isEnabled: false,
				itemKey: 'service-accounts',
			},
		],
	},
	{
		key: 'authentication',
		title: 'Authentication',
		items: [
			{
				key: ROUTES.ORG_SETTINGS,
				label: 'Single Sign-on',
				icon: <User size={16} />,
				isEnabled: false,
				itemKey: 'sso',
			},
		],
	},
	{
		key: 'billing-license',
		title: 'Billing & License',
		items: [
			{
				key: ROUTES.BILLING,
				label: 'Billing',
				icon: <Receipt size={16} />,
				isEnabled: false,
				itemKey: 'billing',
			},
			{
				key: ROUTES.LIST_LICENSES,
				label: 'Manage Licenses',
				icon: <FileKey2 size={16} />,
				isEnabled: false,
				itemKey: 'manage-license',
			},
		],
	},
	{
		key: 'personal',
		title: 'Personal',
		hasDivider: true,
		items: [
			{
				key: ROUTES.MY_SETTINGS,
				label: 'Account',
				icon: <User size={16} />,
				isEnabled: true,
				itemKey: 'account',
			},
			{
				key: ROUTES.SHORTCUTS,
				label: 'Keyboard Shortcuts',
				icon: <Keyboard size={16} />,
				isEnabled: true,
				itemKey: 'keyboard-shortcuts',
			},
		],
	},
];

export const getHelpSupportDropdownMenuItems = (
	t: TFunction,
): SidebarItem[] => [
	{
		key: 'documentation',
		label: (
			<div className="nav-item-label-container">
				<span>{t('helpSupport:documentation').toString()}</span>
				<ArrowUpRight size={14} />
			</div>
		),
		icon: <Book size={14} />,
		isExternal: true,
		url: 'https://signoz.io/docs',
		itemKey: 'documentation',
	},
	{
		key: 'github',
		label: (
			<div className="nav-item-label-container">
				<span>{t('helpSupport:github').toString()}</span>
				<ArrowUpRight size={14} />
			</div>
		),

		icon: <Github size={14} />,
		isExternal: true,
		url: 'https://github.com/signoz/signoz',
		itemKey: 'github',
	},
	{
		key: 'slack',
		label: (
			<div className="nav-item-label-container">
				<span>{t('helpSupport:community_slack').toString()}</span>
				<ArrowUpRight size={14} />
			</div>
		),
		icon: <Slack size={14} />,
		isExternal: true,
		url: 'https://signoz.io/slack',
		itemKey: 'community-slack',
	},
	{
		key: 'chat-support',
		label: t('helpSupport:chat_with_support').toString(),
		icon: <MessageSquareText size={14} />,
		itemKey: 'chat-support',
	},
	{
		key: 'invite-collaborators',
		label: t('helpSupport:invite_team_member').toString(),
		icon: <Plus size={14} />,
		itemKey: 'invite-collaborators',
	},
];

export const settingsNavItemKeyMap: Record<string, string> = {
	workspace: 'routes:data_retention',
	account: 'routes:account',
	'notification-channels': 'routes:alert_channels',
	'sop-documents': 'routes:sop_documents',
	billing: 'routes:billing',
	integrations: 'routes:integrations',
	'mcp-server': 'routes:mcp_server',
	'ai-module': 'routes:ai_module',
	'code-rca': 'routes:code_rca',
	'pii-patterns': 'routes:pii_patterns',
	'remediation-targets': 'routes:remediation_targets',
	'incident-report': 'routes:incident_report',
	'weekly-report': 'routes:weekly_report',
	roles: 'routes:roles',
	members: 'routes:members',
	'service-accounts': 'routes:service_accounts',
	ingestion: 'routes:ingestion',
	sso: 'routes:single_sign_on',
	'keyboard-shortcuts': 'routes:keyboard_shortcuts',
	'manage-license': 'settings:manage_license',
};

export const settingsSectionTitleKeyMap: Record<string, string> = {
	data: 'routes:section_data',
	'ai-automation': 'routes:section_ai_automation',
	alerts: 'routes:section_alerts',
	'identity-access': 'routes:identity_access',
	authentication: 'routes:authentication',
	'billing-license': 'routes:section_billing_license',
	personal: 'routes:section_personal',
};

/** Mapping of some newly added routes and their corresponding active sidebar menu key */
export const NEW_ROUTES_MENU_ITEM_KEY_MAP: Record<string, string> = {
	[ROUTES.TRACE]: ROUTES.TRACES_EXPLORER,
	[ROUTES.TRACE_EXPLORER]: ROUTES.TRACES_EXPLORER,
	[ROUTES.LOGS_BASE]: ROUTES.LOGS_EXPLORER,
	[ROUTES.METRICS_EXPLORER_BASE]: ROUTES.METRICS_EXPLORER,
	[ROUTES.INFRASTRUCTURE_MONITORING_BASE]:
		ROUTES.INFRASTRUCTURE_MONITORING_HOSTS,
	[ROUTES.API_MONITORING_BASE]: ROUTES.API_MONITORING,
	[ROUTES.MESSAGING_QUEUES_BASE]: ROUTES.MESSAGING_QUEUES_OVERVIEW,
};

export default menuItems;
